// Per-edge neighbour data for the terrain shaders (coasts, biome blends,
// river channels). Borrowed from threejs-hex-map (Bunkerbewohner, MIT,
// src/MapMesh.ts): every tile instance carries its six neighbours' terrain as
// instanced attributes plus bitmasks of which edges face water, and the tile
// shader draws shorelines and colour transitions per edge.
//
// Adapted to this board of discrete raised tiles: the masks only change
// colours inside each tile's own top (heights, gaps and chamfer are
// untouched; elevation.ts stays the single height source).
//
// Edge i of a tile is the side between hex corners i and i+1, the corners
// sitting at 30° + 60°·i in the XZ plane (makeTileGeometry, TerrainWater).
// Its outward normal points at 60° + 60°·i.

import * as THREE from 'three';
import { LAND_TOP_LIFT, tileSurfaceY } from './elevation.js';
import { coastKindFor } from './shore/shoreKind.js';

export const WATER_BIOMES: ReadonlySet<string> = new Set(['ocean', 'coast', 'river']);
/** Open water that gets a beach on the land side. Rivers get their own channel. */
const SEA_BIOMES: ReadonlySet<string> = new Set(['ocean', 'coast']);

/**
 * Largest plateau height step (world units) still blended across. Higher
 * steps show a wall anyway, and a colour bleed over a cliff looks wrong.
 */
export const BLEND_MAX_STEP = 0.1;

/** Outward normal (x, z) of edge i: angle 60° + 60°·i. */
export function edgeNormal(i: number): [number, number] {
  const a = ((60 + 60 * i) * Math.PI) / 180;
  return [Math.cos(a), Math.sin(a)];
}

/** World-space centre offset of an axial step (pointy-top, see axialToWorld). */
function axialOffset(dq: number, dr: number): [number, number] {
  return [Math.sqrt(3) * (dq + dr / 2), 1.5 * dr];
}

const AXIAL_DIRS: ReadonlyArray<readonly [number, number]> = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

/**
 * Axial step (dq, dr) to the neighbour across edge i, derived from the edge
 * normals so the masks can never disagree with the geometry.
 */
export const EDGE_NEIGHBOURS: ReadonlyArray<readonly [number, number]> = Array.from(
  { length: 6 },
  (_, i) => {
    const [nx, nz] = edgeNormal(i);
    let best = AXIAL_DIRS[0]!;
    let bestDot = Number.NEGATIVE_INFINITY;
    for (const d of AXIAL_DIRS) {
      const [ox, oz] = axialOffset(d[0], d[1]);
      const dot = (ox * nx + oz * nz) / Math.hypot(ox, oz);
      if (dot > bestDot) {
        bestDot = dot;
        best = d;
      }
    }
    return best;
  },
);

export interface EdgeMasks {
  /** Edges facing ocean or coast water (land tiles: beach; water tiles: unused). */
  readonly sea: number;
  /** Edges facing a river tile. */
  readonly river: number;
  /** Edges facing a land tile. */
  readonly land: number;
  /** Edges facing a land tile whose plateau is within BLEND_MAX_STEP: colour-blended. */
  readonly blend: number;
  /** Subset of `sea` that ends in a cliff rather than a beach (shore/shoreKind.ts). */
  readonly cliff: number;
}

/** 6-bit per-edge masks for the tile at (q, r). Off-map edges set no bits. */
export function edgeMasks(q: number, r: number, biomeMap: ReadonlyMap<string, string>): EdgeMasks {
  const map = biomeMap as Map<string, string>;
  const self = biomeMap.get(`${q},${r}`) ?? 'plains';
  const selfIsLand = !WATER_BIOMES.has(self);
  const selfY = selfIsLand ? tileSurfaceY(q, r, map) : 0;
  let sea = 0;
  let river = 0;
  let land = 0;
  let blend = 0;
  for (let i = 0; i < 6; i++) {
    const [dq, dr] = EDGE_NEIGHBOURS[i]!;
    const nq = q + dq;
    const nr = r + dr;
    const nb = biomeMap.get(`${nq},${nr}`);
    if (nb === undefined) continue;
    const bit = 1 << i;
    if (SEA_BIOMES.has(nb)) sea |= bit;
    else if (nb === 'river') river |= bit;
    else {
      land |= bit;
      if (selfIsLand && Math.abs(tileSurfaceY(nq, nr, map) - selfY) <= BLEND_MAX_STEP) blend |= bit;
    }
  }
  // One beach/cliff verdict per tile: every sea edge of a tile is the same kind.
  const cliff =
    sea !== 0 && selfIsLand && coastKindFor(self, selfY - LAND_TOP_LIFT, q, r) === 'cliff'
      ? sea
      : 0;
  return { sea, river, land, blend, cliff };
}

/** Pack an sRGB-ish 0..1 colour into one float (exact: 24 bits fit a float32 mantissa). */
export function packRGB(r: number, g: number, b: number): number {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  return c(r) * 65536 + c(g) * 256 + c(b);
}

/** Inverse of packRGB (the shaders do the same with floor/mod). */
export function unpackRGB(packed: number): [number, number, number] {
  const p = Math.round(packed);
  const r = Math.floor(p / 65536);
  const g = Math.floor((p - r * 65536) / 256);
  const b = p - r * 65536 - g * 256;
  return [r / 255, g / 255, b / 255];
}

/** GLSL twin of unpackRGB, for vertex shaders. */
export const GLSL_UNPACK_RGB = /* glsl */ `
vec3 _unpackRGB(float p) {
  p = floor(p + 0.5);
  float r = floor(p / 65536.0);
  float g = floor((p - r * 65536.0) / 256.0);
  float b = p - r * 65536.0 - g * 256.0;
  return vec3(r, g, b) / 255.0;
}`;

/**
 * Fog-of-war tone for a tile, as [lightness, saturation] multipliers: the
 * same factors TerrainTiles / TerrainWater apply to instance colours on the
 * CPU, so shader-side colours (sand, foam, neighbour tints) dim with the tile.
 */
export function fogTone(
  key: string,
  visibleCoords: ReadonlySet<string> | null | undefined,
  exploredCoords: ReadonlySet<string> | undefined,
): [number, number] {
  if (!visibleCoords || visibleCoords.has(key)) return [1, 1];
  return exploredCoords?.has(key) ? [0.55, 0.4] : [0.25, 0.15];
}

export interface TileEdgeAttributes {
  /**
   * vec4 per instance: sea mask (bits 0..5, cliff subset in bits 6..11), river mask, land mask, blend mask
   * (as floats, exact in float32).
   */
  readonly masks: Float32Array;
  /** vec3 per instance: packed neighbour base colours across edges 0..2. */
  readonly nbA: Float32Array;
  /** vec3 per instance: packed neighbour base colours across edges 3..5. */
  readonly nbB: Float32Array;
  /**
   * vec3 per instance: fog tone (lightness, saturation); z is left for the
   * tile's GLOBAL index, which TerrainTiles writes after this call — it is
   * the lookup key into the per-tile flag texture (tileData.ts, P7.13).
   * The flags used to pack into z here; the attribute budget and the
   * float-exact ceiling (511) are why they moved.
   */
  readonly tone: Float32Array;
}

/**
 * Instanced attribute arrays for a batch of tiles. `baseColors` maps "q,r" to
 * a tile's packed base colour (biome tint + variation, no fog); an edge with
 * no land neighbour falls back to the tile's own colour, so it never blends.
 */
export function buildTileEdgeAttributes(
  coords: ReadonlyArray<readonly [number, number]>,
  biomeMap: ReadonlyMap<string, string>,
  baseColors: ReadonlyMap<string, number>,
  visibleCoords?: ReadonlySet<string> | null,
  exploredCoords?: ReadonlySet<string>,
): TileEdgeAttributes {
  const n = coords.length;
  const masks = new Float32Array(n * 4);
  const nbA = new Float32Array(n * 3);
  const nbB = new Float32Array(n * 3);
  const tone = new Float32Array(n * 3);
  coords.forEach(([q, r], t) => {
    const key = `${q},${r}`;
    const m = edgeMasks(q, r, biomeMap);
    masks.set([m.sea | (m.cliff << 6), m.river, m.land, m.blend], t * 4);
    const own = baseColors.get(key) ?? 0;
    for (let i = 0; i < 6; i++) {
      const [dq, dr] = EDGE_NEIGHBOURS[i]!;
      const nb = (m.land >> i) & 1 ? (baseColors.get(`${q + dq},${r + dr}`) ?? own) : own;
      if (i < 3) nbA[t * 3 + i] = nb;
      else nbB[t * 3 + i - 3] = nb;
    }
    tone.set(fogTone(key, visibleCoords, exploredCoords), t * 3);
    // z intentionally untouched: TerrainTiles writes the tile's global index
    // here (the texture lookup key); the flags live in tileData.ts now.
  });
  return { masks, nbA, nbB, tone };
}

/**
 * Put a per-instance attribute on a geometry, reusing its buffer when the
 * size matches (a new buffer only when the instance count changes).
 */
export function setInstancedAttribute(
  geometry: THREE.BufferGeometry,
  name: string,
  data: Float32Array,
  itemSize: number,
): void {
  const existing = geometry.getAttribute(name) as THREE.InstancedBufferAttribute | undefined;
  if (existing && existing.array.length === data.length) {
    (existing.array as Float32Array).set(data);
    existing.needsUpdate = true;
    return;
  }
  geometry.setAttribute(name, new THREE.InstancedBufferAttribute(data, itemSize));
}
